import { http, HttpResponse } from 'msw';
import { findUserByEmail } from '../data/users';
import { hashSync, compareSync } from 'bcryptjs';
// import { ApiResponse } from '../../types/api';
import { User, LoginCredentials } from '../../types';

// Helper function to create error responses
const createErrorResponse = (message: string, code: number, status: number) => {
  return HttpResponse.json({
    success: false,
    data: null,
    message,
    code
  }, { status });
};

// Mock JWT token for demonstration
const generateMockToken = (user: User): string => {
  return `mock-jwt-token-${user.id}-${Date.now()}`;
};

export const authHandlers = [
  // Login endpoint
  http.post('/api/v1/auth/login', async ({ request }) => {
    try {
      const credentials = await request.json() as LoginCredentials;
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Find user by email
      const user = findUserByEmail(credentials.email);
      
      if (!user) {
        return createErrorResponse('Invalid email or password', 401, 401);
      }
      
      // 使用 bcryptjs 进行安全密码校验（仅用于模拟环境）
      // 在真实服务端，密码哈希与校验由后端处理
      const validPasswordHashes: Record<string, string> = {
        // 超级管理员：support@ntsports.tech / holo#Motion
        'support@ntsports.tech': hashSync('holo#Motion', 10),
        // 组织管理员：保持原来的演示账号
        'org.admin@example.com': hashSync('orgadmin123', 10),
        'user1@example.com': hashSync('user123', 10),
        'user2@example.com': hashSync('user123', 10)
      };

      const storedHash = validPasswordHashes[credentials.email];
      if (!storedHash || !compareSync(credentials.password, storedHash)) {
        return createErrorResponse('Invalid email or password', 401, 401);
      }
      
      const token = generateMockToken(user);
      
      return HttpResponse.json({
        success: true,
        data: {
          user,
          token
        },
        message: 'Login successful'
      });
      
    } catch (error) {
      return createErrorResponse('Invalid request format', 400, 400);
    }
  }),

  // Logout endpoint
  http.post('/api/v1/auth/logout', async () => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    return HttpResponse.json({
      success: true,
      data: null,
      message: 'Logout successful'
    });
  }),

  // Validate token endpoint
  http.post('/api/v1/auth/validate', async ({ request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      
      if (!authHeader || !authHeader.startsWith('Bearer ')) {
        return createErrorResponse('Invalid token', 401, 401);
      }
      
      const token = authHeader.substring(7);
      
      // Simple token validation (extract user ID from mock token)
      const tokenMatch = token.match(/mock-jwt-token-(\d+)-/);
      if (!tokenMatch) {
        return createErrorResponse('Invalid token format', 401, 401);
      }
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      return HttpResponse.json({
        success: true,
        data: { valid: true },
        message: 'Token is valid'
      });
      
    } catch (error) {
      return createErrorResponse('Token validation failed', 401, 401);
    }
  }),

  // Refresh token endpoint
  http.post('/api/v1/auth/refresh', async ({ request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      
      if (!authHeader || !authHeader.startsWith('Bearer ')) {
        return createErrorResponse('Invalid token', 401, 401);
      }
      
      const token = authHeader.substring(7);
      const tokenMatch = token.match(/mock-jwt-token-(\d+)-/);
      
      if (!tokenMatch) {
        return createErrorResponse('Invalid token format', 401, 401);
      }
      
      const userId = tokenMatch[1];
      const newToken = `mock-jwt-token-${userId}-${Date.now()}`;
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      return HttpResponse.json({
        success: true,
        data: { token: newToken },
        message: 'Token refreshed successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Token refresh failed', 401, 401);
    }
  })
];